// Dates and times the portal shows, in the page's language (<html lang>, the portal locale) for any language
// Intl knows. The server renders English inside a <time datetime> (PortalTimeHelper); the portal-time
// controller rewrites it with these for every other language. Times are shown in the zone the server rendered
// them in, so "today" means the same day on both sides.

import { pageLocale } from './index'

export { pageLocale }

type DateInput = string | number | Date

interface Options {
  locale?: string
  timeZone?: string
  now?: Date
}

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/
const SECONDS = { minute: 60, hour: 3600, day: 86_400, month: 2_592_000, year: 31_536_000 }

// A date with no time ('2026-11-04') is a calendar day: it is read and shown in UTC so no zone shifts it.
function parse(value: DateInput): { date: Date; dateOnly: boolean } {
  if (typeof value === 'string' && DATE_ONLY.test(value)) {
    return { date: new Date(`${value}T00:00:00Z`), dateOnly: true }
  }
  return { date: value instanceof Date ? value : new Date(value), dateOnly: false }
}

function zoneFor(dateOnly: boolean, timeZone?: string): string | undefined {
  return dateOnly ? 'UTC' : timeZone
}

// Days since 1970 of the calendar day `date` falls on in `timeZone`.
function dayNumber(date: Date, timeZone?: string): number {
  const parts = new Intl.DateTimeFormat('en-US', { timeZone, year: 'numeric', month: 'numeric', day: 'numeric' })
    .formatToParts(date)
  const part = (type: string) => Number(parts.find((p) => p.type === type)?.value)
  return Date.UTC(part('year'), part('month') - 1, part('day')) / SECONDS.day / 1000
}

function capitalize(text: string, locale: string): string {
  return text.charAt(0).toLocaleUpperCase(locale) + text.slice(1)
}

// FriendlyDate's rule: today, tomorrow or yesterday; the weekday within about a week; otherwise a short date.
export function formatFriendlyDate(value: DateInput, options: Options & { standalone?: boolean } = {}): string {
  const { locale = pageLocale(), timeZone, now = new Date(), standalone = false } = options
  const { date, dateOnly } = parse(value)
  const zone = zoneFor(dateOnly, timeZone)
  const days = dayNumber(date, zone) - dayNumber(now, timeZone)
  let text: string
  if (Math.abs(days) <= 1) {
    text = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' }).format(days, 'day')
  } else if (days > -7 && days <= 7) {
    text = new Intl.DateTimeFormat(locale, { timeZone: zone, weekday: 'long' }).format(date)
  } else {
    text = new Intl.DateTimeFormat(locale, { timeZone: zone, weekday: 'short', month: 'short', day: 'numeric' })
      .format(date)
  }
  return standalone ? capitalize(text, locale) : text
}

// time_ago_in_words: how long ago, without "ago" ("3 days"), for copy that says "%{time} ago" itself.
export function formatDuration(value: DateInput, options: Options = {}): string {
  const { locale = pageLocale(), now = new Date() } = options
  const seconds = Math.max(0, (now.getTime() - parse(value).date.getTime()) / 1000)
  let unit: keyof typeof SECONDS
  if (seconds < 45 * SECONDS.minute) unit = 'minute'
  else if (seconds < 24 * SECONDS.hour) unit = 'hour'
  else if (seconds < 30 * SECONDS.day) unit = 'day'
  else if (seconds < 365 * SECONDS.day) unit = 'month'
  else unit = 'year'
  const count = Math.max(1, Math.round(seconds / SECONDS[unit]))
  return new Intl.NumberFormat(locale, { style: 'unit', unit, unitDisplay: 'long' }).format(count)
}

// A day and month in the language's order: 11/4 in English, 04/11 in French.
export function formatShortDate(value: DateInput, options: Options = {}): string {
  const { locale = pageLocale(), timeZone } = options
  const { date, dateOnly } = parse(value)
  return new Intl.DateTimeFormat(locale, { timeZone: zoneFor(dateOnly, timeZone), month: 'numeric', day: 'numeric' })
    .format(date)
}

// A date in the language's medium style (4 nov. 2026, 2026/11/04).
export function formatDate(value: DateInput, options: Options = {}): string {
  const { locale = pageLocale(), timeZone } = options
  const { date, dateOnly } = parse(value)
  return new Intl.DateTimeFormat(locale, { timeZone: zoneFor(dateOnly, timeZone), dateStyle: 'medium' }).format(date)
}

export function formatLongDate(value: DateInput, options: Options = {}): string {
  const { locale = pageLocale(), timeZone } = options
  const { date, dateOnly } = parse(value)
  return new Intl.DateTimeFormat(locale, { timeZone: zoneFor(dateOnly, timeZone), dateStyle: 'long' }).format(date)
}

// A clock time in the language's own style (3:05 PM, 15:05). `compact` is the drop-off hours' short form: no
// space before AM/PM, and no ":00" on the hour in 12-hour languages (9AM, 9:30AM; a 24-hour 9:00 stays 9:00).
export function formatTime(value: DateInput, options: Options & { compact?: boolean } = {}): string {
  const { locale = pageLocale(), timeZone, compact = false } = options
  const { date } = parse(value)
  const parts = new Intl.DateTimeFormat(locale, { timeZone, hour: 'numeric', minute: '2-digit' }).formatToParts(date)
  // A plain space before AM/PM, as toLocaleTimeString writes it (Intl's own is a narrow no-break space).
  if (!compact) return parts.map((part) => part.value).join('').replace(/\u202f/g, ' ')

  const twelveHour = parts.some((part) => part.type === 'dayPeriod')
  const onTheHour = parts.find((part) => part.type === 'minute')?.value === '00'
  return parts
    .filter((part, index) => {
      const next = parts[index + 1]
      if (part.type === 'literal' && /^\s+$/.test(part.value) && next?.type === 'dayPeriod') return false
      if (twelveHour && onTheHour && (part.type === 'minute' || (part.type === 'literal' && next?.type === 'minute'))) {
        return false
      }
      return true
    })
    .map((part) => part.value)
    .join('')
}

// A message time: the clock time today, otherwise the time and a short month and day.
export function formatMessageTime(value: DateInput, options: Options = {}): string {
  const { locale = pageLocale(), timeZone, now = new Date() } = options
  const { date } = parse(value)
  const time = formatTime(date, { locale, timeZone })
  if (dayNumber(date, timeZone) === dayNumber(now, timeZone)) return time

  const day = new Intl.DateTimeFormat(locale, { timeZone, month: 'short', day: 'numeric' }).format(date)
  return `${time}, ${day}`
}
